import {
  Button,
  Dialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Grid,
  IconButton,
  Select,
  Typography
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  WithStyles,
  withStyles
} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PropTypes from 'prop-types';
import React from 'react';
import locale from '../locale/Localization';
import {
  CONTRACT_REGISTRATION,
  CONTRACT_TERMINATION,
  SIGNATURE_DECLARATION
} from '../utils/constants';
import DocumentValidation from './DocumentValidation';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2)
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    }
  });

const useStyles = makeStyles((theme: Theme) => ({
  textField: {
    marginRight: theme.spacing(1),
    width: 250,
    marginLeft: 10,
    paddingBottom: 15
  },
  error: {
    color: theme.palette.error.dark
  },
  errorBox: {
    marginTop: 15
  },
  select: {width: 250}
}));

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const {children, classes, onClose, ...other} = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant='h6'>{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label='close'
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2)
  }
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1)
  }
}))(MuiDialogActions);

/**
 * Customized dialog for NotarySuccessor.
 *
 * @param {*} props - Dialog attributes passed as property.
 * @return {string} - The component content.
 */
// eslint-disable-next-line max-lines-per-function, require-jsdoc
export default function NotaryContractDialog(props) {
  const classes = useStyles();
  const [documentType, setDocumentType] = React.useState(
    props.profileMode ? SIGNATURE_DECLARATION : ''
  );
  const [profileMode] = React.useState(props.profileMode);
  const [file, setFile] = React.useState(null);
  const [validationResult, setValidationResult] = React.useState();

  /**
   * Handle file changes.
   *
   * @param {Event} event - The file change event.
   */
  const handleFileChange = (event: Event) => {
    const input = event.target;
    if (input.files.length === 1) {
      setFile(input.files[0]);
    } else {
      setFile(null);
    }
  };

  const handleSubmit = () => {
    props.handleSubmit(documentType, file).then((result) => {
      setValidationResult(result);
    });
  };

  return (
    <div>
      <Dialog
        disableBackdropClick
        aria-labelledby='customized-dialog-title'
        open={props.open}
        maxWidth={'sm'}
        onClose={props.handleClose}
      >
        <DialogTitle id='customized-dialog-title' onClose={props.handleClose}>
          {locale.notaryContractDialogTitle}
        </DialogTitle>
        <DialogContent>
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={12}>
              {!profileMode && locale.notaryContractDialogDescription}
              {profileMode &&
                locale.notarySignatureDeclarationnDialogDescription}
            </Grid>
            {!profileMode && (
              <Grid item xs={6}>
                <Select
                  native
                  onChange={(event) => {
                    setDocumentType(event.target.value);
                  }}
                  className={classes.select}
                  name='documentType'
                  value={documentType}
                  inputProps={{
                    id: 'documentType'
                  }}
                >
                  <option value='' />
                  <option value={CONTRACT_REGISTRATION}>
                    {locale.registrationDocument}
                  </option>
                  <option value={CONTRACT_TERMINATION}>
                    {locale.terminationDocument}
                  </option>
                  <option value={SIGNATURE_DECLARATION}>
                    {locale.signatureDeclarationDocument}
                  </option>
                </Select>
              </Grid>
            )}
            <Grid item xs={6}>
              <input
                id='file-input'
                required
                accept='.pdf'
                type='file'
                name='file'
                onChange={(event) => {
                  handleFileChange(event);
                }}
              />
            </Grid>
          </Grid>
          {validationResult && validationResult.info && (
            <div className={classes.errorBox}>
              <span id='upload-message' className={classes.error}>
                {locale.documentValidationFailed}
              </span>
              <DocumentValidation
                profileMode={props.profileMode}
                validationResult={validationResult}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={props.handleClose}
            color='primary'
            variant='outlined'
          >
            {locale.cancelButton}
          </Button>
          <Button
            autoFocus
            disabled={!file || !documentType}
            onClick={() => handleSubmit()}
            color='primary'
            variant='contained'
          >
            {locale.confirmButton}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

NotaryContractDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  profileMode: PropTypes.bool
};
